// src/pages/Admin/Views/SellerManagement/SellerCommsSection.jsx
import React from 'react';
// Import shadcn/ui components
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import { Badge } from "../../../components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "../../../components/ui/card";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Smartphone, Globe, AlertTriangle, Ban } from "lucide-react";

// Statuses that mean a contact point is currently blocked/at-risk for delivery.
const BLOCKED_HEALTH_STATUSES = new Set(["INVALID", "RISKY", "COMPLAINED", "UNSUBSCRIBED"]);

// Helper to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  // Using Intl for better date formatting
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(dateString));
};

const SellerCommsSection = ({ commsData }) => {
  if (!commsData) return null;

  const {
    health_records = [],
    consents = [],
    unsubscribes = [],
    device_registrations = {},
  } = commsData;

  const {
    pwa_active = 0,
    android_active = 0,
    ios_active = 0,
    total_active = 0,
  } = device_registrations;

  const blockedHealthRecords = health_records.filter((r) => BLOCKED_HEALTH_STATUSES.has(r.status));
  const blockedCount = blockedHealthRecords.length + unsubscribes.length;

  const getHealthBadgeVariant = (status) => {
    switch (status) {
      case 'ACTIVE': return 'default';
      case 'COMPLAINED': return 'destructive';
      case 'UNSUBSCRIBED': return 'destructive';
      case 'INVALID': return 'destructive';
      case 'RISKY': return 'secondary';
      default: return 'secondary';
    }
  };

  return (
    <Card className="mt-4">
        <CardHeader>
            <CardTitle className="flex items-center gap-2">
                Communications
                {blockedCount > 0 && (
                    <Badge variant="destructive" className="gap-1 text-xs font-normal">
                        <AlertTriangle className="h-3 w-3" />
                        {blockedCount} blocked
                    </Badge>
                )}
            </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
            {/* Registered devices - always visible, no click needed */}
            <div className="flex flex-wrap items-center gap-4 text-sm p-2 rounded-md bg-muted/50">
                <div className="flex items-center gap-1.5 font-semibold text-foreground">
                    <Smartphone className="h-4 w-4 text-muted-foreground" />
                    {total_active} device{total_active === 1 ? '' : 's'} registered for push
                </div>
                {total_active > 0 && (
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> Web: {pwa_active}</span>
                        <span>Android: {android_active}</span>
                        <span>iOS: {ios_active}</span>
                    </div>
                )}
            </div>

            {/* Blocked-notifications summary */}
            {blockedCount > 0 && (
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        {blockedHealthRecords.length > 0 && (
                            <>{blockedHealthRecords.length} contact point{blockedHealthRecords.length === 1 ? '' : 's'} with delivery issues (bounce/complaint/invalid). </>
                        )}
                        {unsubscribes.length > 0 && (
                            <>{unsubscribes.length} blanket unsubscribe{unsubscribes.length === 1 ? '' : 's'} on record. </>
                        )}
                        See details below.
                    </AlertDescription>
                </Alert>
            )}

            <Accordion type="single" collapsible className="w-full">
                {/* Accordion Item for Contact Health */}
                <AccordionItem value="item-1">
                    <AccordionTrigger>
                        <div className="flex items-center gap-2">
                            <span>Contact Point Health</span>
                            <Badge variant="secondary">{health_records.length}</Badge>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        {health_records.length > 0 ? (
                            <div className="space-y-2">
                                {health_records.map((record) => (
                                    <div key={record.contact_point + record.channel} className="text-sm p-2 rounded-md bg-muted/50">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <span className="font-semibold text-foreground">{record.channel}: </span>
                                                <span className="text-muted-foreground">{record.contact_point}</span>
                                            </div>
                                            <Badge variant={getHealthBadgeVariant(record.status)}>{record.status}</Badge>
                                        </div>
                                        {record.status_reason && BLOCKED_HEALTH_STATUSES.has(record.status) && (
                                            <div className="mt-1 text-xs text-destructive">
                                                Blocked: {record.status_reason}
                                                {record.status_source ? ` (via ${record.status_source})` : ''}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : <p className="text-sm text-muted-foreground">No contact health records found.</p>}
                    </AccordionContent>
                </AccordionItem>

                {/* Accordion Item for Consent Status */}
                <AccordionItem value="item-2">
                    <AccordionTrigger>
                         <div className="flex items-center gap-2">
                            <span>Consent Status</span>
                            <Badge variant="secondary">{consents.length}</Badge>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        {consents.length > 0 ? (
                            <div className="space-y-2">
                                {consents.map((consent, index) => (
                                    <div key={index} className="text-sm p-2 rounded-md bg-muted/50">
                                        <div className="flex justify-between items-center">
                                            <span className="font-semibold text-foreground">{consent.channel}</span>
                                            <Badge variant={consent.is_active ? "default" : "destructive"}>
                                                {consent.is_active ? 'Active' : 'Revoked'}
                                            </Badge>
                                        </div>
                                        <div className="text-muted-foreground mt-1">
                                            Type: {consent.consent_type} | Source: {consent.source} | Expires: {formatDate(consent.expires_at)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : <p className="text-sm text-muted-foreground">No consent records found.</p>}
                    </AccordionContent>
                </AccordionItem>

                {/* Accordion Item for Blanket Unsubscribes */}
                <AccordionItem value="item-3">
                    <AccordionTrigger>
                         <div className="flex items-center gap-2">
                            <Ban className="h-4 w-4 text-muted-foreground" />
                            <span>Unsubscribed</span>
                            <Badge variant={unsubscribes.length > 0 ? "destructive" : "secondary"}>{unsubscribes.length}</Badge>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        {unsubscribes.length > 0 ? (
                            <div className="space-y-2">
                                {unsubscribes.map((unsub, index) => (
                                    <div key={index} className="text-sm p-2 rounded-md bg-muted/50">
                                        <div className="flex justify-between items-center">
                                            <span className="font-semibold text-foreground">{unsub.channel} / {unsub.comms_category}</span>
                                            <span className="text-xs text-muted-foreground">{formatDate(unsub.created_at)}</span>
                                        </div>
                                        {unsub.reason && (
                                            <div className="text-muted-foreground mt-1">Reason: {unsub.reason}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : <p className="text-sm text-muted-foreground">No unsubscribes on record.</p>}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </CardContent>
    </Card>
  );
};

export default SellerCommsSection;
